import React, { useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import {
  Routes,
  Route,
  BrowserRouter as Router,
} from 'react-router-dom';
import styled from 'styled-components';
import { useAppSelector } from './hooks';
import './App.css';
import HomePage from './components/HomePage';
import LoginPage from './components/LoginPage';
import GoogleCallback from './components/LoginPage/GoogleCallback';
import UserManagePage from './components/UserManagePage';
import NotificationContainer from './components/Notification/NotificationContainer';
import Header from './components/Header';
import { pathConstants } from './constants';
import FlashCardPDF from './components/FlashCardPDF/FlashCardPDF';
import RandomTeamPage from './components/RandomTeamPage';
import MatchWordMeaning from './components/MatchWordMeaning/MatchWordMeaning';
import ClapGame from './components/ClapGame/ClapGame';
import ClassesManage from './components/ClassesManage';
import StudentsManage from './components/StudentsManage';
import StudentInformation from './components/StudentsManage/StudentInformation';
import QuestionsManage from './components/QuestionsManage';
import ExamsManage from './components/ExamsManage';
import AttendanceManage from './components/AttendanceManage/AttendanceManage';
import StudentPage from './components/StudentPage';
import TuitionManageNew from './components/TuitionManageNew/TuitionManage';
import TuitionStatistics from './components/TuitionStatistics';
import SchedulesManage from './components/SchedulesManage';
import LessonDiary from './components/LessonDiaryManage';
import { ProtectedRoute } from './helpers/ProtectedRoute';
import PublicExamPage from './components/PublicExamPage';
import RetryExamPage from './components/StudentPage/RetryExamPage';
import FlashcardDashboard from './components/Flashcard/FlashcardDashboard';
import SetEditor from './components/Flashcard/SetEditor';
import LearnMode from './components/Flashcard/LearnMode';
import SetDetailPage from './components/Flashcard/SetDetailPage';
import FlashcardQuizPage from './components/Flashcard/FlashcardQuizPage';
import MatchingGamePageWrapper from './components/Flashcard/MatchingGamePageWrapper';
import DocumentLibrary from './components/DocumentLibrary/DocumentLibrary';
import VocabularyManage from './components/VocabularyManage';
import CreateExamPage from './components/ExamsManage/CreateExamPage';
import ReviewDetailPage from './components/ReviewManage/ReviewDetailPage';
import ReviewDashboard from './components/ReviewManage/ReviewDashboard';
import StudentReviewListPage from './components/StudentPage/StudentReviewListPage';
import SentenceOrderPage from './components/TeachingMode/SentenceOrderPage';
import MultipleChoicePage from './components/TeachingMode/MultipleChoicePage';
import IELTSPracticePage from './components/IELTSPractice/IELTSPracticePage';
import BankAccountManage from './components/BankAccountManage/BankAccountManage';
import VocabGameHostPage from './components/VocabGame/VocabGameHostPage';
import VocabGameJoinPage from './components/VocabGame/VocabGameJoinPage';
import StudentPickerPage from './components/StudentPicker/StudentPickerPage';
import GiftManage from './components/GiftManage/GiftManage';

export const AppWrapper = styled.div`
  position: relative;
  min-height: 400px;
`;

const SnowContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  overflow: hidden;
  width: 100vw;
  height: 100vh;
  z-index: 99999;
  pointer-events: none;
`;

const App: React.FC = () => {
  return (
    <Router>
      <AppWrapper>
        <SnowContainer className="snow-container" />
        <NotificationContainer />
        <Routes>
          {/* Các route không có Header */}
          <Route path="/" element={<HomePage />} />
          <Route path={pathConstants.LOGIN} element={<LoginPage />} />
          <Route path="/auth/google/callback" element={<GoogleCallback />} />
          <Route path={pathConstants.STUDENT_PAGE} element={<StudentPage />} />
          <Route path={pathConstants.EXAM_PUBLIC} element={<PublicExamPage />} />
          <Route path="/retry-exam" element={<RetryExamPage />} />
          <Route path={pathConstants.STUDENT_REVIEW_LIST} element={<StudentReviewListPage />} />

          {/* Vocab Game — public routes (no auth needed) */}
          <Route path="/join" element={<VocabGameJoinPage />} />

          {/* Student flashcard routes - without header */}
          <Route path="/student/flashcards" element={<FlashcardDashboard />} />
          <Route path="/student/flashcards/folders/:folderId" element={<FlashcardDashboard />} />
          <Route path="/student/flashcards/sets/:id/detail" element={<SetDetailPage />} />
          <Route path="/student/flashcards/sets/:id" element={<LearnMode />} />
          <Route path="/student/flashcards/sets/:id/learn" element={<LearnMode />} />
          <Route path="/student/flashcards/sets/:id/quiz" element={<FlashcardQuizPage />} />
          <Route path="/student/flashcards/sets/:id/matching" element={<MatchingGamePageWrapper />} />

          {/* Các route có Header - được khai báo trong AppWithHeader */}
          <Route path={pathConstants.MATCH_WORD_MEANING} element={<AppWithHeader><MatchWordMeaning /></AppWithHeader>} />
          <Route path={pathConstants.CLAP_GAME} element={<AppWithHeader><ClapGame /></AppWithHeader>} />
          <Route path={pathConstants.CLASSES_MANAGE} element={<AppWithHeader><ClassesManage /></AppWithHeader>} />
          <Route path={pathConstants.SCHEDULES_MANAGE} element={<AppWithHeader><SchedulesManage /></AppWithHeader>} />
          <Route path={pathConstants.STUDENTS_MANAGE} element={<AppWithHeader><StudentsManage /></AppWithHeader>} />
          <Route path={`${pathConstants.STUDENTS_MANAGE}/:id`} element={<AppWithHeader><StudentInformation /></AppWithHeader>} />
          <Route path={pathConstants.QUESTIONS_MANAGE} element={<AppWithHeader><QuestionsManage /></AppWithHeader>} />
          <Route path={pathConstants.EXAMS_MANAGE} element={<AppWithHeader><ExamsManage /></AppWithHeader>} />
          <Route path={`${pathConstants.EXAMS_MANAGE}/create`} element={<AppWithHeader><CreateExamPage /></AppWithHeader>} />
          <Route path={pathConstants.RANDOM_TEAM} element={<AppWithHeader><RandomTeamPage /></AppWithHeader>} />
          <Route path={pathConstants.FLASHCARD_PDF_CREATE} element={<AppWithHeader><FlashCardPDF /></AppWithHeader>} />
          <Route path={pathConstants.LESSON_DIARY_MANAGE} element={<AppWithHeader><LessonDiary /></AppWithHeader>} />
          <Route path={pathConstants.ATTENDANCE_MANAGE} element={<AppWithHeader><AttendanceManage /></AppWithHeader>} />
          <Route path={pathConstants.TUITION_MANAGE_NEW} element={<AppWithHeader><TuitionManageNew /></AppWithHeader>} />
          <Route path={pathConstants.TUITION_STATISTICS} element={<AppWithHeader><TuitionStatistics /></AppWithHeader>} />
          <Route path={pathConstants.USER_MANAGE} element={<AppWithHeader><UserManagePage /></AppWithHeader>} />

          {/* Document Library routes */}
          <Route path="/documents" element={<AppWithHeader><DocumentLibrary /></AppWithHeader>} />

          {/* Flashcard routes - protected (requires login for manage features) */}
          <Route path="/flashcards" element={<AppWithHeader><FlashcardDashboard /></AppWithHeader>} />
          <Route path="/flashcards/folders/:folderId" element={<AppWithHeader><FlashcardDashboard /></AppWithHeader>} />
          <Route path="/flashcards/sets/new" element={<AppWithHeader><SetEditor /></AppWithHeader>} />
          <Route path="/flashcards/sets/:id/edit" element={<AppWithHeader><SetEditor /></AppWithHeader>} />
          {/* Flashcard view routes - public (no login required) */}
          <Route path="/flashcards/sets/:id/detail" element={<SmartFlashcardWrapper><SetDetailPage /></SmartFlashcardWrapper>} />
          <Route path="/flashcards/sets/:id/quiz" element={<SmartFlashcardWrapper><FlashcardQuizPage /></SmartFlashcardWrapper>} />
          <Route path="/flashcards/sets/:id/matching" element={<SmartFlashcardWrapper><MatchingGamePageWrapper /></SmartFlashcardWrapper>} />
          <Route path="/flashcards/sets/:id" element={<SmartFlashcardWrapper><LearnMode /></SmartFlashcardWrapper>} />
          <Route path="/flashcards/sets/:id/learn" element={<SmartFlashcardWrapper><LearnMode /></SmartFlashcardWrapper>} />

          {/* Vocabulary Management */}
          <Route path="/vocabulary" element={<AppWithHeader><VocabularyManage /></AppWithHeader>} />

          {/* Review Sessions - public detail page (like flashcard sets) */}
          <Route path="/review/:id" element={<SmartFlashcardWrapper><ReviewDetailPage /></SmartFlashcardWrapper>} />

          {/* Teaching Mode */}
          <Route path={pathConstants.SENTENCE_ORDER} element={<AppWithHeader><SentenceOrderPage /></AppWithHeader>} />
          <Route path={pathConstants.MULTIPLE_CHOICE} element={<AppWithHeader><MultipleChoicePage /></AppWithHeader>} />
          <Route path={pathConstants.STUDENT_PICKER} element={<AppWithHeader><StudentPickerPage /></AppWithHeader>} />
          <Route path={pathConstants.GIFT_MANAGE} element={<AppWithHeader><GiftManage /></AppWithHeader>} />

          {/* Review Management - admin/teacher only */}
          <Route path="/review-manage" element={<AppWithHeader><ReviewDashboard /></AppWithHeader>} />

          {/* IELTS Practice — accessible to all users */}
          <Route path={pathConstants.IELTS_PRACTICE} element={<AppWithHeader><IELTSPracticePage /></AppWithHeader>} />

          {/* Bank Account Management — admin only */}
          <Route path={pathConstants.BANK_ACCOUNT_MANAGE} element={<AppWithHeader><BankAccountManage /></AppWithHeader>} />

          {/* Vocab Game Host — teacher/admin only */}
          <Route path="/game/host/:setId" element={<SmartFlashcardWrapper><VocabGameHostPage /></SmartFlashcardWrapper>} />
        </Routes>
      </AppWrapper>
    </Router>
  );
};
const Wrapper = styled.div`
  display: flex;
  min-height: 100vh;
  `;

const SidebarWrapper = styled.div<{ expand: boolean }>`
  width: ${(props) => (props.expand ? '260px' : '70px')};
  transition: all 0.25s ease-in-out;
  background-color: #0e2238;

  /* Mobile: Sidebar không chiếm diện tích khi đóng */
  @media (max-width: 768px) {
    width: 0;
    min-width: 0;
  }
  `;

const MainContent = styled.div`
  flex: 1;
  background-color: #fafbfe;
  padding: 1rem;
  transition: all 0.35s ease-in-out;

  /* Mobile: Full width */
  @media (max-width: 768px) {
    margin-left: 0;
    padding-top: 60px; /* Chừa chỗ cho toggle button */
  }
  `;
/**
 * SmartFlashcardWrapper: used for flashcard view routes (detail, learn, quiz, matching).
 * - Admin / Teacher (role 0, 1): full layout WITH sidebar, no auth redirect
 * - Student (role 2) / Guest (not logged in): simple layout WITHOUT sidebar, no auth redirect
 */
const SmartFlashcardWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [expand, setExpand] = useState(() => window.innerWidth > 768);
  const userInfo = useAppSelector((state: any) => state.authentication.userInfo);
  const isAdminOrTeacher = userInfo?.role === 0 || userInfo?.role === 1;

  if (isAdminOrTeacher) {
    // Show full layout with sidebar (same as AppWithHeader but without ProtectedRoute)
    return (
      <Wrapper>
        <SidebarWrapper expand={expand}>
          <Header expand={expand} onExpandChange={setExpand} />
        </SidebarWrapper>
        <MainContent>{children}</MainContent>
      </Wrapper>
    );
  }

  // Student or guest: simple layout, no sidebar, no auth redirect
  return (
    <Wrapper>
      <MainContent>{children}</MainContent>
    </Wrapper>
  );
};

const AppWithHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Check if mobile and set default expand state
  const [expand, setExpand] = useState(() => {
    return window.innerWidth > 768; // Desktop: mở, Mobile: đóng
  });

  // Get user role
  const userInfo = useAppSelector((state: any) => state.authentication.userInfo);
  const isStudent = userInfo?.role === 2;

  return (
    <ProtectedRoute>
      <Wrapper>
        {/* Hide sidebar if student (role = 2) */}
        {!isStudent && (
          <SidebarWrapper expand={expand}>
            <Header expand={expand} onExpandChange={setExpand} />
          </SidebarWrapper>
        )}

        {/* MainContent không bị re-render khi `expand` thay đổi */}
        <React.Fragment>
          <MainContent>{children}</MainContent>
        </React.Fragment>
      </Wrapper>
    </ProtectedRoute>
  );
};


export default App;
